import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { PageHeader } from "@/components/shared";
import ScopeOfPractice from "@/components/ScopeOfPractice";

const steps = [
  { t: "Enquiry", d: "You reach out through the booking form or a message. A few short intake questions help me understand, gently, what's bringing you here — nothing you're not ready to share." },
  { t: "Pre-session clarity", d: "Before we begin, we identify the one issue you'd most like to work with. A clear starting point makes for focused, meaningful work." },
  { t: "The session itself", d: "This is where the work happens. In a group, participants represent elements of your system; in individual work, we use figures or anchors. It runs 1.5–2 hours. You are never required to disclose family details publicly." },
  { t: "The close", d: "Every constellation is brought to a gentle, complete close. We don't leave anything open or unsettled. You leave with the arrangement resolved as far as it wishes to go that day." },
  { t: "Integration", d: "The real work often continues quietly over the following days and weeks. I'll share what to expect, and a few gentle things to do — and not do — as things settle." },
  { t: "Follow-up", d: "Sometimes a second session deepens the work; often it isn't needed at all. I'll always be honest about what I feel would genuinely serve you." },
];

export default function HowItWorks() {
  useSeo({
    title: "How a Constellation Session Works, Step by Step",
    description: "From enquiry to integration — exactly what happens in a Family Constellation session with Arjunna Soni, so you know what to expect before you book.",
    path: "/how-it-works",
  });
  return (
    <div>
      <PageHeader
        crumb="How It Works"
        title="What happens, step by step"
        intro="Not knowing what will happen in that room is the biggest reason people hesitate. So here is exactly how it goes — start to finish."
      />

      <section className="container-page pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <h2 className="text-2xl md:text-3xl text-earth mb-8" style={{ fontWeight: 600 }}>The six stages of a Family Constellation session</h2>
            <ol className="relative border-l-2 border-hairline ml-2">
              {steps.map((s, i) => (
                <li key={i} className="relative pl-8 pb-12 last:pb-0" data-testid={`process-step-${i}`}>
                  <span className="absolute -left-[11px] top-1 w-5 h-5 rounded-full bg-gold border-4 border-parchment" />
                  <span className="label-caps text-earth/50">Step {String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-2xl md:text-3xl text-earth mt-1 mb-2" style={{ fontWeight: 600 }}>{s.t}</h3>
                  <p className="text-lg text-earth/75">{s.d}</p>
                </li>
              ))}
            </ol>

            <div className="mt-6"><ScopeOfPractice /></div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 bg-surface-card border hairline rounded-lg p-7">
              <h3 className="text-xl text-earth mb-2">Ready when you are</h3>
              <p className="text-earth/70 mb-5">A first session takes about 1.5–2 hours. Booking takes under four minutes.</p>
              <Link to="/book" className="btn-gold w-full">Book a Session</Link>
              <Link to="/services" className="btn-outline w-full mt-3">View services & pricing</Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
